import React, { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Brush, Tooltip, Legend, Cell, ResponsiveContainer, Label, ReferenceLine, CartesianGrid } from 'recharts';
import { convertSingleValue, japaneseTickFormatter } from '../utils/commonUtils';


const range = (y) => {
    const yMax = Math.max(...y);
    const yMin = Math.min(...y);
    const range = yMax - yMin
    if (yMin > 0) {
        const rangeMax = yMax * 1.2
        const rangeMin = 0;
        return [rangeMin, rangeMax]
    } else if (yMax < 0) {
        const rangeMax = 0
        const rangeMin = yMin * 1.2;
        return [rangeMin, rangeMax]
    } else {
        const rangeMax = yMax + range * 0.1
        const rangeMin = yMin - range * 0.1
        return [rangeMin, rangeMax]
    }
}

const SummaryQuartelryBarGraph = ({ data, unit, height = 500, width, showLabel}) => {
    const [isSmallArea, setIsSmallArea] = useState(false);
    const [formattedData, setFormattedData] = useState([]);
    const [activeIndex, setActiveIndex] = useState(null);

    const handleMouseEnter = (_, index) => {
        setActiveIndex(index)
    }

    const handleMouseLeave = () => {
        setActiveIndex(null)
    }

    useEffect(() => {
        if (!data || data.length === 0) {
            console.error('No data provided to SummaryQuarterlyBarGraph');
            return;
        }

        const groupedData = data.reduce((acc, item) => {
            const [year, quarter] = item.x.split(" ");
            if (!acc[year]) {
                acc[year] = [];
            }
            acc[year].push({ ...item, quarter: quarter || "FY" });
            return acc;
        }, {});

        const newFormattedData = Object.entries(groupedData).flatMap(([year, items]) => {
            return items.map((item, index) => ({
                ...item,
                year,
            }));
        });

        setFormattedData(newFormattedData);
    }, [data]);

    
    useEffect(() => {
        if (width / data.length < 80 ) {
            setIsSmallArea(true);
        } else {
            setIsSmallArea(false);
        }
    }, [width, data])

    const stopColor = {
        start: "rgba(45, 186, 177, .5)", 
        end: "rgba(45, 186, 77, .5)"
    };

    const stopColorHover = {
        start: "rgba(45, 186, 177, .8)", 
        end: "rgba(45, 186, 77, .8)"
    };

    const CustomLabel = ({ x, y, width, value, index }) => {
        const offset = value >= 0 ? -10 : 20;
        return (
            <text
            x={x + width / 2}
            y={y + offset} 
            fill="black"
            textAnchor="middle"
            fontSize={14}
            >
                {convertSingleValue(data[index]['y'])}{unit}
            </text>
        );
    };
    
    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            return (
                <div className="custom-tooltip text-white px-2 py-1 border border-gray-300 bg-gray-600 rounded shadow">
                    {payload.map((item, index) => {
                        return (
                            <div key={index}>
                                {`${item.payload.x}: ${convertSingleValue(item.payload[item['dataKey']])}`}{unit}
                            </div>
                        );
                    })}
                </div>
            );
        }
        return null;
    };

    
    return (
        <ResponsiveContainer width="100%" height={height}>
            <BarChart
                data={formattedData}
                margin={{ top: 20, right: 30, left: 30, bottom: 30 }}
                barCategoryGap="20%"
                barGap={5}
            >
                {/* グラデーションの定義 */}
                <defs>
                    <linearGradient id="gradient0" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={stopColor.start} />
                        <stop offset="95%" stopColor={stopColor.end} />
                    </linearGradient>
                    <linearGradient id="gradient1" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={stopColorHover.start} />
                        <stop offset="95%" stopColor={stopColorHover.end} />
                    </linearGradient>
                </defs>
                {((showLabel === 'auto' && isSmallArea) || showLabel==='hidden') && <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />}
                <XAxis
                    dataKey="x"
                    height={60}
                    tick={(props) => {
                        const { x, y, payload, index } = props;
                        const [year, quarter] = payload.value.split(" ");
                        const showYear = index === 0 || (formattedData[index - 1] && formattedData[index - 1].year !== year);
                        const quarterToShow = quarter || "FY";
                        return (
                            <g transform={`translate(${x},${y})`}>
                                <text x={0} y={0} dy={15} textAnchor="middle" fill="#666" fontSize={12}>
                                    {quarterToShow}
                                </text>
                                {showYear && (
                                    <text x={0} y={0} dy={35} textAnchor="middle" fill="#666" fontSize={12}>
                                        {year}
                                    </text>
                                )}
                            </g>
                        );
                    }}
                />
                {((showLabel === 'auto' && isSmallArea) || showLabel==='hidden') &&
                    <YAxis tickFormatter={(value) => japaneseTickFormatter(value, unit)}
                        tickCount={4}
                        domain={range(data.map((dt) => dt.y))}
                     >
                        <Label
                            position="top"
                            offset={20}
                            style={{ textAnchor: 'middle', fill: 'grey' }}
                        />
                    </YAxis>
                }
                <ReferenceLine y={0} stroke="rgb(100, 100, 100)" />
                <Tooltip
                    content={<CustomTooltip />}
                    cursor={false}
                />
                <Bar
                    dataKey="y"
                    fill={`url(#gradient0)`}  // グラデーションを適用
                    label={((!isSmallArea && showLabel==='auto') || showLabel==='visible') ? <CustomLabel unit={unit} /> : null}
                    radius={isSmallArea ? [3, 3, 0, 0] : [5, 5, 0, 0]}
                    animationBegin={0}
                    animationDuration={1000}
                    animationEasing="ease-in-out"
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                >
                    {data.map((_, index) => (
                        <Cell
                        key={`cell-${index}`}
                        opacity={activeIndex === null || index === activeIndex ? 1 : 0.3}
                        />
                        
                    ))}
                </Bar>
                <Brush dataKey="x" height={15} stroke="lightgrey" />
            </BarChart>
        </ResponsiveContainer>
    );
};

export default SummaryQuartelryBarGraph;
